// ============================================================================
// BESPOKIAN Health - Reusable Step Cards Component
// Luxury numbered step cards with gold accents
// ============================================================================

export interface Step {
  title: string;
  description: string;
  badgeColor?: string;
}

interface StepCardsProps {
  steps: Step[];
  kicker?: string;
  title?: string;
  subtitle?: string;
  showNumbers?: boolean;
  className?: string;
  variant?: 'light' | 'dark';
}

const defaultBadgeColors = [
  'bg-gold-500',
  'bg-sage-500',
  'bg-navy-700',
  'bg-gold-600',
  'bg-sage-600',
  'bg-navy-600',
];

export function StepCards({
  steps,
  kicker,
  title,
  subtitle,
  showNumbers = true,
  className = '',
  variant = 'light',
}: StepCardsProps) {
  const isDark = variant === 'dark';

  return (
    <section className={`py-20 ${isDark ? 'bg-navy-800' : 'bg-white'} ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(kicker || title || subtitle) && (
          <div className="text-center mb-16">
            {/* Gold accent line */}
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            {kicker && (
              <p
                className={`font-medium tracking-wide uppercase text-sm mb-4 ${
                  isDark ? 'text-gold-400' : 'text-gold-600'
                }`}
              >
                {kicker}
              </p>
            )}

            {title && (
              <h2 className={`font-display text-3xl md:text-4xl italic tracking-tight mb-4 ${
                isDark ? 'text-white' : 'text-navy-800'
              }`}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className={`text-lg max-w-2xl mx-auto ${
                isDark ? 'text-navy-200' : 'text-navy-600'
              }`}>
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const badgeColor = step.badgeColor || defaultBadgeColors[index % defaultBadgeColors.length];
            
            return (
              <div key={step.title} className="relative">
                {showNumbers && (
                  <div
                    className={`absolute -top-4 left-6 w-10 h-10 ${badgeColor} rounded-full flex items-center justify-center font-bold shadow-lg ${
                      badgeColor.includes('gold') ? 'text-navy-800' : 'text-white'
                    }`}
                  >
                    {index + 1}
                  </div>
                )}
                <div className={`rounded-2xl p-8 ${showNumbers ? 'pt-10' : ''} h-full border transition-shadow hover:shadow-card ${
                  isDark
                    ? 'bg-navy-700/50 border-navy-600'
                    : 'bg-navy-50/50 border-navy-100'
                }`}>
                  <h3 className={`font-display text-xl italic tracking-tight mb-3 ${
                    isDark ? 'text-white' : 'text-navy-800'
                  }`}>
                    {step.title}
                  </h3>
                  <p className={isDark ? 'text-navy-200' : 'text-navy-600'}>
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


